import React, { useEffect, useMemo, useRef, useState } from 'react';
import { folioService } from '../../../services/folioService';

// Folio's company picker — ONE component at the top of both tabs (Model and Review). Closed: the selected company and
// its needs-you count. Open: a search box (ticker or name, case-insensitive; Enter on an exact ticker opens it at once)
// over the covered companies — "Needs review" first (most questions first), then "All companies" A–Z with each one's
// status — and "Add company…" at the bottom. A typed ticker that matches no covered company offers "Add <TICKER> to
// Folio…" (the dialog opens with it looked up). Keyboard: ↑ ↓ Enter, Esc closes. Phones: a full-width sheet.

export const TICKER_SHAPE = /^(?=.*[A-Z])[A-Z.-]{1,10}$/;      // the backend's whitelist (folio_views.ADD_TICKER_RE)

// the default company when the address names none: the most questions that need the owner, else the first A–Z
export function defaultTicker(companies) {
  if (!companies.length) return null;
  const top = companies.filter((c) => c.needs_you > 0).sort((a, b) => b.needs_you - a.needs_you || a.ticker.localeCompare(b.ticker))[0];
  return (top || [...companies].sort((a, b) => a.ticker.localeCompare(b.ticker))[0]).ticker;
}

function Status({ c }) {
  const bits = [];
  if (c.loading) bits.push(<span key="l" className="text-sky-600 dark:text-sky-300">loading</span>);
  if (c.waiting) bits.push(<span key="w">{c.waiting} waiting</span>);
  if (c.failing_checks) bits.push(<span key="f" className="text-red-600 dark:text-red-400">{c.failing_checks} failing checks</span>);
  // a subset of the failing checks, shown beside them (register #234) — never subtracted
  if (c.company_error_proven) bits.push(<span key="ce" className="text-amber-600 dark:text-amber-400">{c.company_error_proven} company error, proven</span>);
  if (c.catalogue && c.catalogue !== 'corporate') bits.push(<span key="c">{c.catalogue}</span>);
  if (!bits.length) bits.push(<span key="ok">up to date</span>);
  return <span className="flex flex-wrap justify-end gap-x-2 text-[11px] text-gray-500 dark:text-gray-400">{bits}</span>;
}

function NeedsChip({ n }) {
  if (!n) return null;
  return <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 font-mono text-[11px] tabular-nums text-amber-700 dark:text-amber-300"
    title={`${n} question(s) need you`}>{n}</span>;
}

export function AddCompanyDialog({ initialTicker = '', onClose, onAdded }) {
  const [ticker, setTicker] = useState(initialTicker);
  const [preview, setPreview] = useState(null);
  const [fy, setFy] = useState(2019);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);
  const lookup = async (raw) => {
    const t = String(raw || '').trim().toUpperCase();
    setPreview(null); setErr(''); setDone(null);
    if (!TICKER_SHAPE.test(t)) { setErr('A ticker is letters, "." or "-", at most 10.'); return; }
    setBusy(true);
    try {
      const r = await folioService.previewCompany(t);
      setPreview(r.data);
      setFy(r.data.first_fy_default || 2019);
    } catch (e) { setErr(e?.response?.data?.detail || 'Could not look it up.'); } finally { setBusy(false); }
  };
  useEffect(() => { if (initialTicker) lookup(initialTicker); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async () => {
    setBusy(true); setErr('');
    try {
      const r = await folioService.addCompany(preview.ticker, preview.cik, fy);
      setDone(r.data);
      onAdded(preview.ticker);
    } catch (e) { setErr(e?.response?.data?.detail || 'Could not add it.'); } finally { setBusy(false); }
  };
  const years = [];
  if (preview) for (let y = preview.first_fy_max; y >= preview.first_fy_min; y -= 1) years.push(y);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center" role="dialog" aria-modal="true"
      aria-label="Add a company" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}>
      <div className="w-full max-w-md rounded-t-2xl border border-gray-200 bg-white p-4 shadow-xl dark:border-gray-800 dark:bg-[#161b22] sm:rounded-2xl">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-lg text-gray-900 dark:text-gray-100">Add a company</h2>
          <button type="button" onClick={onClose} className="rounded-md px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">Close</button>
        </div>
        <form className="mt-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); lookup(ticker); }}>
          <input autoFocus value={ticker} onChange={(e) => setTicker(e.target.value.toUpperCase())} maxLength={10}
            placeholder="Ticker, e.g. FITB" aria-label="Ticker"
            className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-transparent px-3 py-2 font-mono text-sm uppercase text-gray-900 dark:border-gray-700 dark:text-gray-100" />
          <button type="submit" disabled={busy}
            className="rounded-lg border border-brand-gold/60 px-3 py-2 text-sm text-brand-gold disabled:opacity-40">Look up</button>
        </form>
        {err && <div className="mt-2 text-sm text-red-600 dark:text-red-400">{err}</div>}
        {preview && !done && (
          <div className="mt-3 space-y-3 text-sm text-gray-700 dark:text-gray-300">
            <div><span className="font-medium text-gray-900 dark:text-gray-100">{preview.name}</span>
              <span className="text-gray-500"> · CIK {preview.cik} · from SEC&apos;s company list</span></div>
            {preview.already_covered ? <div className="text-amber-700 dark:text-amber-300">Already covered — pick it in the list.</div>
              : preview.adding ? <div className="text-sky-700 dark:text-sky-300">Already being added.</div> : (
                <div className="flex flex-wrap items-center gap-2">
                  <label htmlFor="folio-first-fy" className="text-[11px] uppercase tracking-wider text-gray-500">Fiscal years from</label>
                  <select id="folio-first-fy" value={fy} onChange={(e) => setFy(Number(e.target.value))}
                    className="rounded-lg border border-gray-300 bg-transparent px-2 py-1.5 text-sm dark:border-gray-700 dark:bg-[#161b22]">
                    {years.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                  <button type="button" onClick={add} disabled={busy}
                    className="ml-auto rounded-lg bg-brand-gold px-4 py-1.5 text-sm text-black disabled:opacity-40">Add</button>
                </div>
              )}
            <div className="text-xs text-gray-500">Its filings are found, archived, parsed and built by the pipeline in
              its own hours; questions go to the answerers. It shows as loading until then.</div>
          </div>
        )}
        {done && <div className="mt-3 text-sm text-green-700 dark:text-green-400">Queued — {done.ticker} shows as
          loading in the list while its filings are fetched.</div>}
      </div>
    </div>
  );
}

export default function CompanyPicker({ companies, pending = [], value, onChange, onAdded }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const [adding, setAdding] = useState(null);       // null = closed, '' = empty dialog, 'FITB' = looked up at once
  const box = useRef(null);
  const listRef = useRef(null);
  const sel = companies.find((c) => c.ticker === value);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const { groups, flat, exact, typed } = useMemo(() => {
    const s = q.trim().toLowerCase();
    const hit = (c) => !s || c.ticker.toLowerCase().includes(s) || (c.name || '').toLowerCase().includes(s)
      || (c.sec_name || '').toLowerCase().includes(s);
    const shown = companies.filter(hit);
    const needs = shown.filter((c) => c.needs_you > 0)
      .sort((a, b) => b.needs_you - a.needs_you || a.ticker.localeCompare(b.ticker));
    const all = [...shown].sort((a, b) => a.ticker.localeCompare(b.ticker));
    const pend = pending.filter((t) => !s || t.toLowerCase().includes(s));
    const up = q.trim().toUpperCase();
    const exactHit = companies.find((c) => c.ticker === up) || null;
    const canAdd = !exactHit && TICKER_SHAPE.test(up) && !pending.includes(up);
    const g = [];
    if (needs.length) g.push({ title: 'Needs review', rows: needs.map((c) => ({ kind: 'co', c })) });
    g.push({ title: 'All companies', rows: [...all.map((c) => ({ kind: 'co', c })),
      ...pend.map((t) => ({ kind: 'pending', t }))] });
    const tail = [];
    if (canAdd) tail.push({ kind: 'add', t: up });
    tail.push({ kind: 'add', t: '' });
    g.push({ title: '', rows: tail });
    const f = g.flatMap((x) => x.rows).filter((r) => r.kind !== 'pending');
    return { groups: g, flat: f, exact: exactHit, typed: up };
  }, [companies, pending, q]);

  useEffect(() => { setActive(0); }, [q, open]);
  useEffect(() => {
    const el = listRef.current?.querySelector('[data-active="true"]');
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const choose = (r) => {
    if (!r) return;
    if (r.kind === 'co') { onChange(r.c.ticker); setOpen(false); setQ(''); }
    if (r.kind === 'add') { setAdding(r.t); setOpen(false); }
  };
  const onKey = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, flat.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (exact && typed) choose({ kind: 'co', c: exact });            // an exact ticker opens at once
      else choose(flat[active]);
    }
  };

  let idx = -1;
  return (
    <div ref={box} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={open}
        className="flex min-h-[44px] w-full max-w-full items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/60 sm:min-h-0 hover:border-brand-gold/60 dark:border-gray-800 dark:bg-[#161b22] sm:w-auto sm:min-w-[320px]">
        {value ? <span className="font-mono text-sm text-gray-900 dark:text-gray-100">{value}</span> : null}
        <span title={sel?.sec_name || undefined} className="min-w-0 flex-1 truncate font-serif text-sm text-gray-700 dark:text-gray-300">{value ? (sel?.name || '')
          : <span className="font-sans text-gray-500 dark:text-gray-400">Choose a company</span>}</span>
        <NeedsChip n={sel?.needs_you} />
        {sel?.loading && <span className="text-[11px] text-sky-600 dark:text-sky-300">loading</span>}
        <span className="text-gray-400">▾</span>
      </button>
      {open && (
        <div className="fixed inset-x-0 bottom-0 z-40 flex max-h-[80vh] flex-col rounded-t-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-800 dark:bg-[#161b22] sm:absolute sm:inset-x-auto sm:bottom-auto sm:left-0 sm:top-full sm:mt-1 sm:max-h-[70vh] sm:w-[440px] sm:rounded-xl">
          <div className="border-b border-gray-200 p-2 dark:border-gray-800">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
              placeholder="Ticker or company" aria-label="Search companies" role="combobox" aria-expanded="true"
              aria-controls="folio-company-list"
              className="w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:text-gray-100" />
          </div>
          <div ref={listRef} id="folio-company-list" role="listbox" className="scrollbar-thin flex-1 overflow-y-auto p-1">
            {groups.map((g, gi) => (
              <div key={gi}>
                {g.title && <div className="px-2 pb-1 pt-2 text-[10px] uppercase tracking-wider text-gray-500">{g.title}</div>}
                {g.title && !g.rows.length && <div className="px-2 py-1 text-xs text-gray-500">No match.</div>}
                {g.rows.map((r) => {
                  if (r.kind === 'pending') {
                    return (
                      <div key={`p-${r.t}`} className="flex items-center gap-2 px-2 py-2 text-sm text-gray-500">
                        <span className="font-mono">{r.t}</span><span className="flex-1">being added</span>
                        <span className="text-[11px] text-sky-600 dark:text-sky-300">loading</span>
                      </div>
                    );
                  }
                  idx += 1;
                  const me = idx;
                  const on = me === active;
                  const cls = `flex min-h-[44px] w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm sm:min-h-0 ${on ? 'bg-brand-gold/10' : 'hover:bg-gray-100 dark:hover:bg-gray-800/60'}`;
                  if (r.kind === 'add') {
                    return (
                      <button type="button" key={`a-${r.t}`} role="option" aria-selected={on} data-active={on}
                        onMouseEnter={() => setActive(me)} onClick={() => choose(r)} className={`${cls} text-brand-gold`}>
                        {r.t ? `Add ${r.t} to Folio…` : '+ Add company…'}
                      </button>
                    );
                  }
                  const c = r.c;
                  return (
                    <button type="button" key={`${g.title}-${c.ticker}`} role="option" aria-selected={on} data-active={on}
                      onMouseEnter={() => setActive(me)} onClick={() => choose(r)} className={cls}>
                      <span className="w-14 shrink-0 font-mono text-gray-900 dark:text-gray-100">{c.ticker}</span>
                      <span title={c.sec_name || undefined} className="min-w-0 flex-1 truncate text-gray-700 dark:text-gray-300">{c.name}</span>
                      {g.title === 'Needs review' ? <NeedsChip n={c.needs_you} /> : <><NeedsChip n={c.needs_you} /><Status c={c} /></>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
      {adding !== null && (
        <AddCompanyDialog initialTicker={adding} onClose={() => setAdding(null)}
          onAdded={(t) => { if (onAdded) onAdded(t); }} />
      )}
    </div>
  );
}
